import { Button, Drawer, Modal, Popconfirm, Spin, Menu, Tooltip, message } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getPolicyBreakdowns, controller as treatmentsController } from "../../controller";
import { controller as rootController } from "../../../../controller";
import { AI_FETCH_METHODS, supportsAny, carrierCodeFromPolicy } from "../../lib/carrierCapabilities";
import BreakdownDetailsModal from "./BreakdownDetailsModal";
import BreakdownInProgressRow from "./BreakdownInProgressRow";
import BreakdownRequestModal from "./BreakdownRequestModal";
import SheetHeader from "./sheet/SheetHeader";
import PlanSummary from "./sheet/PlanSummary";
import CoverageTable from "./sheet/CoverageTable";
import PlanEditPanel from "./sheet/PlanEditPanel";
import { categoryCardsOf } from "./sheet/sheetModel";
import ManualBreakdownWizard from "./manual/ManualBreakdownWizard";
import { showsIntake, useViewerRole } from "../../../viewerRole";
import OutcomeTagControl from "../OutcomeTagControl";
import BreakdownFailureCard from "./BreakdownFailureCard";
import ConfirmRecalculateModal from "./ConfirmRecalculateModal";
import ApproveButton from "./ApproveButton";
import PolicySelect from "../shared/PolicySelect";
import CarrierContactBar from "../shared/CarrierContactBar";
import CarrierPortalCredential from "../shared/CarrierPortalCredential";
import VerificationActionButton from "../shared/VerificationActionButton";
import useRequestComposer from "../../../InsuranceRequests/useRequestComposer";
import HandledByChip from "../shared/HandledByChip";
import BreakdownExportButtons, { formatChoiceOptions } from "./BreakdownExportButtons";
import { CheckCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { breakdownDocumentTitle } from "./print/printDocument";
import { breakdownImageFilename } from "./print/exportFilename";
import { downloadBlob, showManualCopy } from "./print/clipboard";
import {
    NOTHING_TO_EXPORT, runPrintExport, startImageExport, startTextExport,
} from "./print/customFormat/exportActions";
import { isUsableId, useOfficeBreakdownFormat } from "./print/customFormat/officeFormat";
import { currentOfficeId } from "../../../officeFeatures";

export default function InsuranceBreakdownMasterModal({
    showInsuranceBreakdownModal,
    setShowInsuranceBreakdownModal,
    patientPolicies,
    // Optional: pre-select this policy on open (else the first policy).
    defaultSelectedPolicyId,
    // Optional: pre-select this breakdown id in the left list on first load.
    defaultSelectedEntryId,
    // Optional: the agent's suggestion for Details Needed by Smilepass when its
    // fetch failed on policy data — { note, fields } (FTC queue chips).
    outcomeSuggestion,
    patient,
    // The patient's OWN office (threaded for cross-office FTC/super-admin use);
    // falls back to selectedOffice inside CarrierPortalCredential when absent.
    officeId,
  officeIdentity,
    // True while policies are auto-fetched from the PMS for a no-policy patient;
    // shows a spinner in the Select-Policy sidebar until the fetch resolves.
    isFetchingPolicies,
    eligibilityData,
    agentType,
    setLastUploadedBreakdown,
    isGettingDoctorNote,
    handleScraperAgentCall,
    isAgentScrappingData,
    checkPatientInsuranceEligibility,
    breakdownEligibilityRefreshTick = 0,
    breakdownModalEligibilityActive = false,
    setAgentCancelProcess,
    eligibilityAgent2FAStatus = "",
    eligibilityTaskStatus = "",
    onPoliciesChanged,
    onRequestAddPolicy,
    onNoteSaved,
    // The carrier list behind Policy Information's editable Carrier select.
    // Fetched once by InsuranceMasterModal and shared by every tab.
    carrierOptions,
    isLoadingCarriers,
    // Viewer mode — see InsuranceMasterModal. Hides the carrier contact bar, the
    // saved portal login and the Verify / Add Manually / Upload PDF rail.
    hideVerificationTools = false,
    // When true, render only the inner two-pane body (no own <Modal> wrapper,
    // no Close/Print bar) so InsuranceMasterModal can host it as a tab.
    embedded = false,
}) {
    // Every request from this modal goes through the Insurance Verification
    // composer, so it opens a request row before any agent runs — see
    // useRequestComposer for why that matters.
    const { openComposer, composer } = useRequestComposer({ overModal: true });
    // FTC personnel (and super admins) see the intake cues: "Ask", "N to ask
    // the carrier", the fill-in links. Office users — and anyone opening a
    // breakdown from the office's Insurance Verification page (reader mode) —
    // see it as the office does. False until the role is known, so the cues
    // never flash.
    const viewer = useViewerRole();
    const intake = showsIntake(viewer, hideVerificationTools);
    const [selectedPolicyID, setSelectedPolicyID] = useState(undefined);
    const [breakdownsList, setBreakdownsList] = useState([]);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [manualWizardOpen, setManualWizardOpen] = useState(false);

    // Right panel state
    const [selectedBreakdownID, setSelectedBreakdownID] = useState(undefined);
    const [deletingBreakdownId, setDeletingBreakdownId] = useState(undefined);
    const [showNewBreakdownModal, setShowNewBreakdownModal] = useState(false);
    const [newBreakdownID, setNewBreakdownID] = useState(undefined);
    // Consume `defaultSelectedEntryId` only on the first breakdown-list load
    // (deep-link), then fall back to newest so manual policy switches aren't pinned.
    const initialEntryConsumedRef = useRef(false);

    // When incremented, forces BreakdownDetailsModal to remount and refetch
    const [refreshKey, setRefreshKey] = useState(0);

    // Policy-level finance card state (shared across all breakdowns for a policy).
    // The variable name is historical — it now also holds Preventative.
    const [basicMajorOrthoData, setBasicMajorOrthoData] = useState({
        preventative: { overall_max: undefined, coverage: undefined, benefits_remaining: undefined },
        basic: { overall_max: undefined, coverage: undefined, benefits_remaining: undefined },
        major: { overall_max: undefined, coverage: undefined, benefits_remaining: undefined },
        orthodontics: { overall_max: undefined, coverage: undefined, benefits_remaining: undefined },
    });
    // Raw manual per-category overrides on the SELECTED breakdown, so we can
    // merge-edit them (set one / revert one / clear all).
    const [categoryOverrides, setCategoryOverrides] = useState({});
    // Category groups sharing one dollar maximum, e.g. [["Basic","Preventative"]].
    const [sharedMaxGroups, setSharedMaxGroups] = useState([]);
    // The selected breakdown's lines as the cards' fetch returns them — the
    // Insurance profile reads perio/endo rates and ortho ages from them.
    const [coverageRows, setCoverageRows] = useState([]);
    const [planDrawerOpen, setPlanDrawerOpen] = useState(false);
    // Benefit-year end → the annual dollar maximum resets the day after it.
    const [benefitYearEnd, setBenefitYearEnd] = useState(null);
    const maxResetDate = benefitYearEnd
        ? (function () {
            const d = new Date(benefitYearEnd + "T00:00:00");
            d.setDate(d.getDate() + 1);
            return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
        })()
        : null;
    const [isLoadingPolicyCoverage, setIsLoadingPolicyCoverage] = useState(false);
    // Which policy + breakdown the plan section last loaded. A refetch of the
    // same one (after a line is saved) keeps what's on screen — swapping it
    // for a spinner threw away a coverage edit in progress.
    const [coverageLoadedFor, setCoverageLoadedFor] = useState(null);

    // Recalculate flow — all at master level since it's policy-wide, not breakdown-specific
    const [hasBasciMajorOrthoUpdated, setHasBasciMajorOrthoUpdated] = useState(false);
    const [showConfirmRecalculateModal, setShowConfirmRecalculateModal] = useState(false);
    const [confirmRecalculate, setConfirmRecalculate] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [policyOverrides, setPolicyOverrides] = useState({});
    const [isPrintingPDF, setIsPrintingPDF] = useState(false);
    const [isCopyingImage, setIsCopyingImage] = useState(false);
    const [isCopyingText, setIsCopyingText] = useState(false);
    // The office's own breakdown sheet, when it has one live: the exports use
    // it. A super admin can preview a draft or the standard exports instead
    // ("auto" = the live one). The click itself uses the breakdown's office.
    const [formatChoice, setFormatChoice] = useState("auto");
    const formatOfficeId = isUsableId(officeId) ? officeId : currentOfficeId();
    const officeFormat = useOfficeBreakdownFormat(formatOfficeId, isUsableId(formatChoice) ? formatChoice : null);
    const customExports = formatChoice !== "standard" && !!officeFormat.format;

    const [capabilityMatrix, setCapabilityMatrix] = useState(null);
    // Measure the right-side summary (Policy Info + the 4 category cards) so the
    // left "note" card can stretch to match its height and line up at the bottom.
    const summaryRef = useRef(null);
    const [summaryHeight, setSummaryHeight] = useState(0);
    useEffect(() => {
        const el = summaryRef.current;
        if (!el) {
            setSummaryHeight(0);
            return undefined;
        }
        const update = () => setSummaryHeight(el.offsetHeight);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, [selectedPolicyID, isLoadingPolicyCoverage, breakdownsList.length, basicMajorOrthoData]);


    // Load the hardcoded carrier capability matrix once so we can gate the AI Fetch button.
    useEffect(function () {
        var cancelled = false;
        rootController.getCarrierCapabilities()
            .then(function (matrix) { if (!cancelled) setCapabilityMatrix(matrix); })
            .catch(function (err) { console.error("Failed to load carrier capabilities", err); });
        return function () { cancelled = true; };
    }, []);

    // AI phone-call dispatch (voice-agent): shown on the carrier-contact card
    // for call-method carriers the matrix has graduated to ai_phone_call.
    // Progress/results ride the normal ScrapeJob pipeline (drawer, toasts,
    // failure card, BreakdownInProgressRow).
    const [aiCallBusy, setAiCallBusy] = useState(false);
    async function handleAiPhoneCall(policyId) {
        if (!policyId || aiCallBusy) return;
        setAiCallBusy(true);
        try {
            const res = await treatmentsController.startPhoneBreakdownCall(policyId);
            if (res && res.status === 200) {
                message.success("AI call dispatched — the assistant is dialing the carrier.");
            } else if (res && res.status === 503) {
                message.warning((res.json && res.json.error) || "AI calling is unavailable right now.");
            } else {
                message.error((res.json && res.json.error) || "Couldn't start the AI call.");
            }
        } catch (err) {
            console.error("startPhoneBreakdownCall failed", err);
            message.error("Couldn't start the AI call.");
        } finally {
            setAiCallBusy(false);
        }
    }

    // Initialize policy selection — honor a caller-requested policy (e.g. an FTC
    // work-item chip) when it's one this patient actually holds, else default to
    // the first policy.
    useEffect(() => {
        if (patientPolicies && patientPolicies.length > 0) {
            const preferred =
                defaultSelectedPolicyId != null &&
                patientPolicies.some((p) => p.id === defaultSelectedPolicyId)
                    ? defaultSelectedPolicyId
                    : patientPolicies[0].id;
            setSelectedPolicyID(preferred);
        }
    }, [patientPolicies, defaultSelectedPolicyId]);

    // Fetch breakdowns list when policy changes. On the very first load honor a
    // caller-requested breakdown id (deep-link from the "View Breakdown" button);
    // afterwards always fall back to newest so manual policy switches aren't pinned.
    useEffect(() => {
        if (!selectedPolicyID || !patient) return;
        const preferred = initialEntryConsumedRef.current ? null : defaultSelectedEntryId;
        initialEntryConsumedRef.current = true;
        fetchPolicyBreakdowns(selectedPolicyID, preferred);
    }, [selectedPolicyID, patient]);

    // Recompute the 4 coverage-category cards on the fly for whichever breakdown
    // is selected (display-only — the policy's stored coverage isn't touched).
    useEffect(() => {
        if (!selectedPolicyID) return;
        fetchBreakdownCoverage(selectedPolicyID, selectedBreakdownID != null ? selectedBreakdownID : newBreakdownID);
    }, [selectedPolicyID, selectedBreakdownID, newBreakdownID]);

    // If a new breakdown is uploaded/created, reload the list and select it
    useEffect(() => {
        if (newBreakdownID && selectedPolicyID) {
            fetchPolicyBreakdowns(selectedPolicyID, newBreakdownID);
        }
    }, [newBreakdownID, selectedPolicyID]);

    // Trigger recalculation after user confirms in the modal
    useEffect(() => {
        if (confirmRecalculate) {
            performRecalculate();
        }
    }, [confirmRecalculate]);

    // Handle agent fetches populating the view
    useEffect(() => {
        if (agentType === "breakdown" && eligibilityData !== undefined && eligibilityData.id) {
            setNewBreakdownID(eligibilityData.id);
        }
    }, [eligibilityData, agentType]);

    useEffect(() => {
        if (breakdownEligibilityRefreshTick > 0) {
            setRefreshKey((k) => k + 1);
        }
    }, [breakdownEligibilityRefreshTick]);

    async function handleDeleteBreakdown(breakdownId) {
        try {
            setDeletingBreakdownId(breakdownId);
            const res = await rootController.deleteInsuranceBreakdown(breakdownId);
            if (res && res.status < 250) {
                message.success("Breakdown deleted");
                // Clear the selection BEFORE refreshing. fetchPolicyBreakdowns
                // re-selects the newest survivor when no preferred id is passed,
                // so leaving the deleted id in place would strand the right pane
                // on a breakdown that no longer exists.
                setSelectedBreakdownID(undefined);
                setNewBreakdownID(undefined);
                await fetchPolicyBreakdowns(selectedPolicyID);
            } else {
                message.error(
                    (res && res.json && (res.json.error || res.json.detail)) ||
                    "Couldn't delete the breakdown.",
                );
            }
        } catch (err) {
            console.error(err);
            message.error("Couldn't delete the breakdown.");
        } finally {
            setDeletingBreakdownId(undefined);
        }
    }

    async function fetchPolicyBreakdowns(policyId, preferredBreakdownId = null) {
        try {
            setIsLoadingList(true);
            const response = await getPolicyBreakdowns(policyId, patient.id);
            if (response.status < 250) {
                const payload = response.json;
                const breakdowns = Array.isArray(payload) ? payload : (payload.breakdowns || []);
                setBreakdownsList(breakdowns);
                if (preferredBreakdownId != null && breakdowns.some((b) => b.id === preferredBreakdownId)) {
                    setSelectedBreakdownID(preferredBreakdownId);
                } else {
                    // No default concept — select the most-recent breakdown
                    // (the list comes back newest-first).
                    const nextId = breakdowns[0] && breakdowns[0].id;
                    setSelectedBreakdownID(nextId);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoadingList(false);
        }
    }

    // Coverage for the 4 category cards, computed on the fly from the SELECTED
    // breakdown (display-only). The backend returns `category_coverage` keyed by
    // category, each {percentage, overall_max, benefits_remaining}.
    async function fetchBreakdownCoverage(policyId, breakdownId) {
        try {
            setIsLoadingPolicyCoverage(true);
            const response = await treatmentsController.getBreakdownCoverageAndProcedureCoveragesByPolicy(policyId, breakdownId);
            const cards = categoryCardsOf(response.status < 250 && response.json ? response.json.category_coverage : null);
            if (cards) {
                setBasicMajorOrthoData(cards);
                setSharedMaxGroups(
                    (response.json && response.json.category_shared_max_groups) || []
                );
                setCoverageRows((response.json && response.json.procedure_coverages) || []);
                setCategoryOverrides((response.json && response.json.category_overrides) || {});
                // The current period's end (stored dates rolled forward on the
                // server), so the reset date never names a year already over.
                setBenefitYearEnd((response.json && ((response.json.benefit_period || {}).end
                    || response.json.benefit_year_end)) || null);
                // The server's own copy of the plan: saving a line can fill plan
                // fields from the lines (perio & endo, implants, ...), and an
                // earlier local copy must not hide them.
                const fresh = response.json && response.json.policy;
                if (fresh && fresh.id === policyId) {
                    setPolicyOverrides((prev) => ({ ...prev, [policyId]: { ...(prev[policyId] || {}), ...fresh } }));
                }
                setCoverageLoadedFor(`${policyId}-${breakdownId}`);
            }
        } catch (err) {
            console.error("Failed to fetch breakdown coverage", err);
        } finally {
            setIsLoadingPolicyCoverage(false);
        }
    }

    // ----- Manual per-category card overrides (persisted on the breakdown) -----
    const activeBreakdownId = () => (selectedBreakdownID != null ? selectedBreakdownID : newBreakdownID);

    async function persistCategoryOverrides(nextOverrides) {
        const bId = activeBreakdownId();
        if (!bId) {
            message.error("Create or select a breakdown first");
            return false;
        }
        const response = await treatmentsController.updateBreakdownCategoryOverrides(bId, nextOverrides);
        if (response.status < 250) {
            await fetchBreakdownCoverage(selectedPolicyID, bId);
            return true;
        }
        message.error("Couldn't save the override");
        return false;
    }

    // "Add Manually" opens the wizard; the breakdown is created when its first
    // step is saved (handleManualCreated), never before.
    const handleCreateManual = () => {
        if (!selectedPolicyID) {
            message.error("Select an insurance policy first.");
            return;
        }
        setManualWizardOpen(true);
    };

    const handleManualCreated = async (breakdownId) => {
        // The backend clears "Breakdown - Verified" on manual create (a
        // hand-entered breakdown isn't scraper-verified) — mirror it locally so
        // the OutcomeTagControl reflects it immediately.
        if (Array.isArray(patient.tags)) {
            patient.tags = patient.tags.filter((t) => t !== "Breakdown - Verified");
        }
        await fetchPolicyBreakdowns(selectedPolicyID, breakdownId);
    };

    const handleManualFinished = async (breakdownId) => {
        if (!breakdownId) return;
        await fetchPolicyBreakdowns(selectedPolicyID, breakdownId);
        // Already selected since step 1 was saved: its plan and coverage
        // changed in the wizard, so read them again.
        await fetchBreakdownCoverage(selectedPolicyID, breakdownId);
        if (onPoliciesChanged) onPoliciesChanged();
    };

    // A check before recalculation — warn if there are manual card overrides
    // (this session's edits OR persisted ones), since Recalculate reverts them.
    const handleRecalculate = () => {
        const hasOverrides = categoryOverrides && Object.keys(categoryOverrides).length > 0;
        if ((hasBasciMajorOrthoUpdated || hasOverrides) && !confirmRecalculate) {
            setShowConfirmRecalculateModal(true);
            return;
        }
        performRecalculate();
    };

    // Recalculate policy-wide coverage and refresh finance cards + breakdown details
    async function performRecalculate() {
        const workingBreakdownId = selectedBreakdownID || newBreakdownID;
        try {
            setIsApproving(true);
            const response = await treatmentsController.recalculateBreakdown(selectedPolicyID);

            const payload = response.json;
            const recalcOk =
                response.status < 250 &&
                payload &&
                (payload.success === undefined || payload.success === true);

            if (!recalcOk) {
                const errMsg =
                    (payload && (payload.message || payload.error)) ||
                    "Could not recalculate coverage for this policy.";
                message.error(errMsg);
                return;
            }

            if (workingBreakdownId) {
                // Recalculate reverts ALL manual card overrides on this breakdown.
                await treatmentsController.updateBreakdownCategoryOverrides(workingBreakdownId, {});
                await treatmentsController.updateBreakdownItems(workingBreakdownId, { status: "approved" });
            }

            message.success("Recalculated successfully!");

            // Refresh the finance cards for the current selection
            await fetchBreakdownCoverage(selectedPolicyID, workingBreakdownId);

            // Force BreakdownDetailsModal to remount and refetch its breakdown data
            setRefreshKey((k) => k + 1);

            // Reset finance-card-edit tracking
            setConfirmRecalculate(false);
            if (hasBasciMajorOrthoUpdated) {
                setHasBasciMajorOrthoUpdated(false);
            }
        } catch (err) {
            message.error("Something went wrong while recalculating breakdown");
            console.error(err);
        } finally {
            setIsApproving(false);
        }
    }

    const renderSidebar = () => {
        // No "default" breakdown — the left list shows every breakdown and the
        // right side reflects whichever one is selected.
        const otherBreakdowns = breakdownsList;
        // When a manual note is present, stretch the note card so its bottom
        // lines up with the right-side summary (Policy Info + the 4 cards).
        // Note is per-policy now → key the layout off the selected policy's note.
        const _selPolicyForNote = (patientPolicies || []).find((p) => p.id === selectedPolicyID);
        // An agent-sourced note is FTC-only (the card hides it from the office).
        const hasAttentionNote = !!(_selPolicyForNote && _selPolicyForNote.attention_note
            && (intake || _selPolicyForNote.attention_note_source !== "agent"));

        return (
            <div style={{ width: 300, borderRight: "1px solid #f0f0f0", display: "flex", flexDirection: "column", height: "100%" }}>
                {/* Top block — when a note is present, height-matched to the
                    right-side Policy Info + category cards so the note card's
                    bottom lines up with the summary. The note card flexes to fill. */}
                <div
                    style={{
                        // Was flexShrink: 0, which made this block grow without
                        // bound — and .ant-modal-body is overflow:hidden, so
                        // anything past the modal height was simply unreachable
                        // (measured: 1474px of content in 1150px, with the portal
                        // credentials clipped off the bottom entirely). It grew
                        // that tall when the carrier panel gained its knowledge
                        // sections. Now it takes its natural height, yields when
                        // there isn't room, and scrolls what doesn't fit.
                        flex: "0 1 auto",
                        minHeight: hasAttentionNote && summaryHeight ? summaryHeight : 0,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        borderBottom: "1px solid #f0f0f0",
                        background: "#fafbfc",
                    }}
                >
                    <div data-onboarding-id="bd-select-policy" style={{ padding: "16px 15px 10px 15px", flexShrink: 0 }}>
                        <PolicySelect
                            patientPolicies={patientPolicies}
                            value={selectedPolicyID}
                            onChange={(value) => {
                                setSelectedPolicyID(value);
                                setSelectedBreakdownID(undefined);
                                // Reset recalculate state when switching policies
                                setHasBasciMajorOrthoUpdated(false);
                                setConfirmRecalculate(false);
                            }}
                            onRequestAddPolicy={onRequestAddPolicy}
                            isFetchingPolicies={isFetchingPolicies}
                        />
                    </div>

                    {!hideVerificationTools && (() => {
                        const selPolicy = (patientPolicies || []).find((p) => p.id === selectedPolicyID);
                        const selCarrier = selPolicy ? carrierCodeFromPolicy(selPolicy) : null;
                        const phoneCallSupported = !!(capabilityMatrix && selCarrier &&
                            supportsAny(capabilityMatrix, selCarrier, "breakdown", ["ai_phone_call"]));
                        return (
                            <CarrierContactBar
                                identity={officeIdentity}
                                policy={selPolicy}
                                taskType="breakdown"
                                aiCallEnabled={phoneCallSupported}
                                aiCallBusy={aiCallBusy}
                                onAiCall={() => handleAiPhoneCall(selectedPolicyID)}
                            />
                        );
                    })()}
                    {!hideVerificationTools && (
                        <CarrierPortalCredential policy={(patientPolicies || []).find((p) => p.id === selectedPolicyID)} officeId={officeId} />
                    )}

                    {/* Attention surface (manual note / scrape failure). Lives
                     * here between the policy dropdown and the default card so
                     * it doesn't push the right-side finance cards down and
                     * break their alignment with this column. */}
                    <div data-onboarding-id="bd-failure-card" style={{ padding: "0 15px", flex: hasAttentionNote ? 1 : "0 0 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
                        {renderAttentionSurface()}
                    </div>

                </div>

                {/* Scrollable list of non-default breakdowns */}
                <div data-onboarding-id="bd-breakdowns-menu" style={{ flex: 1, overflowY: "auto", padding: 0, minHeight: 0 }}>
                    {selectedPolicyID ? (
                        <BreakdownInProgressRow patientId={patient && patient.id} policyId={selectedPolicyID} />
                    ) : null}
                    {isLoadingList ? (
                        <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>
                    ) : !selectedPolicyID ? null : otherBreakdowns.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "30px 20px", color: "#848696", fontSize: 13 }}>
                            No breakdowns yet.
                        </div>
                    ) : (
                        <>
                            <div style={{ padding: "14px 18px 6px 18px", fontSize: 11, fontWeight: 600, letterSpacing: 1, color: "#848696", textTransform: "uppercase" }}>
                                Breakdowns
                            </div>
                            <Menu
                                selectedKeys={selectedBreakdownID ? [selectedBreakdownID.toString()] : []}
                                mode="inline"
                                style={{ borderRight: 0 }}
                                onClick={({ key }) => setSelectedBreakdownID(parseInt(key))}
                            >
                                {otherBreakdowns.map(item => (
                                    <Menu.Item
                                        key={item.id}
                                        style={{
                                            height: "auto",
                                            padding: "12px 24px",
                                            lineHeight: "1.2",
                                        }}
                                    >
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                            {/* minWidth:0 is load-bearing — a flex item defaults to min-width:auto
                                                and would otherwise refuse to shrink, pushing the actions out of the
                                                rail where they are clipped. That is what hid Delete on the estimates
                                                side, whose metadata line is a little longer than this one. */}
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ fontWeight: 500 }}>
                                                    ID: {item.id} {item.name && `- ${item.name}`}
                                                </div>
                                                <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                                                    {item.created_at || item.upload_date
                                                        ? "Created " + new Date(item.created_at || item.upload_date).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                                                        : "Manual Entry"}
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                                {item.status === 'approved' && <CheckCircleOutlined style={{ color: "#52c41a" }} />}
                                                {/* Only show spinner if AI agent is actively fetching — not for manually created pending breakdowns */}
                                                {item.status === 'processing' && isAgentScrappingData && agentType === 'breakdown' && item.id === newBreakdownID && (
                                                    <Spin size="small" />
                                                )}
                                                {/* Row selection is handled by <Menu onClick> on the parent, so
                                                    every interactive child must stop propagation or deleting would
                                                    also select the row. */}
                                                <span onClick={(e) => e.stopPropagation()}>
                                                    <Popconfirm
                                                        title="Delete this breakdown?"
                                                        description="Its procedure coverages go too. Any treatment plans are kept."
                                                        okText="Delete"
                                                        okButtonProps={{ danger: true, loading: deletingBreakdownId === item.id }}
                                                        cancelText="Cancel"
                                                        onConfirm={() => handleDeleteBreakdown(item.id)}
                                                    >
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            title="Delete breakdown"
                                                            aria-label="Delete breakdown"
                                                            icon={<DeleteOutlined />}
                                                            loading={deletingBreakdownId === item.id}
                                                            style={{ color: "#cf1322" }}
                                                        />
                                                    </Popconfirm>
                                                </span>
                                            </div>
                                        </div>
                                    </Menu.Item>
                                ))}
                            </Menu>
                        </>
                    )}
                </div>

                {!hideVerificationTools && (
                <div style={{ padding: "20px 15px", borderTop: "1px solid #f0f0f0", display: "flex", flexDirection: "column", gap: 10 }}>
                    {(() => {
                        // Compute capability state once for the single
                        // capability-aware verification control: carrier has
                        // an AI agent → "AI Verification"; otherwise →
                        // "Request Verification" + urgent toggle. Order
                        // top-to-bottom is Verify → Add Manually → Upload PDF.
                        const sidebarPolicy = (patientPolicies || []).find(function (p) { return p.id === selectedPolicyID; });
                        const carrier = carrierCodeFromPolicy(sidebarPolicy);
                        const carrierSupportsAIFetch = capabilityMatrix
                            ? supportsAny(capabilityMatrix, carrier, "breakdown", AI_FETCH_METHODS)
                            : true; // Before the matrix loads, don't disable — avoids a flash of disabled.
                        // Only block while a BREAKDOWN is in flight — a running
                        // eligibility/predetermination agent is a separate
                        // operation (separate ScrapeJob slot) and must not
                        // disable breakdown fetching. (Previously this blocked
                        // on any agentType !== 'breakdown', so an in-progress —
                        // or stuck — estimate permanently greyed this out.)
                        const breakdownRunning = isAgentScrappingData && agentType === 'breakdown';

                        let aiDisableReason = null;
                        if (!capabilityMatrix) {
                            aiDisableReason = null;
                        } else if (!carrier) {
                            aiDisableReason = "Select a policy first.";
                        } else if (!carrierSupportsAIFetch) {
                            aiDisableReason = "This carrier doesn't support any AI fetch method for breakdowns.";
                        }
                        const aiTooltip = aiDisableReason || "AI will fetch the breakdown instantly.";

                        return (
                            <div data-onboarding-id="bd-verify-action">
                                <VerificationActionButton
                                    operationType="breakdown"
                                    requestNoun="Breakdown"
                                    patientId={patient && patient.id}
                                    capabilityKnown={!!capabilityMatrix}
                                    policySelected={!!selectedPolicyID}
                                    hasScraper={carrierSupportsAIFetch}
                                    aiTooltip={aiTooltip}
                                    onOpenComposer={() => {
                                        setSelectedBreakdownID(undefined);
                                        openComposer(patient, {
                                            policyId: selectedPolicyID,
                                            requestType: "breakdown",
                                        });
                                    }}
                                />
                            </div>
                        );
                    })()}
                    <Tooltip title="Enter and save a breakdown by hand — useful when you already have the coverage info.">
                        <Button
                            block
                            data-onboarding-id="bd-add-manually"
                            onClick={handleCreateManual}
                        >
                            Add Manually
                        </Button>
                    </Tooltip>
                    <Tooltip title="Upload a PDF breakdown and we'll extract the coverage details for you.">
                        <Button
                            block
                            onClick={() => {
                                setSelectedBreakdownID(undefined);
                                setShowNewBreakdownModal(true);
                            }}
                        >
                            Upload PDF Breakdown
                        </Button>
                    </Tooltip>
                </div>
                )}
            </div>
        );
    };

    const selectedPolicy = useMemo(() => {
        const base = (patientPolicies || []).find((p) => p.id === selectedPolicyID) || null;
        if (!base) return null;
        const overrides = policyOverrides[base.id];
        return overrides ? { ...base, ...overrides } : base;
    }, [patientPolicies, selectedPolicyID, policyOverrides]);

    // "Edit plan": the top of the breakdown, editable (PlanEditPanel).
    const openPlanDetails = () => setPlanDrawerOpen(true);

    // A saved plan: fold the server's copy into the selected policy, refetch the
    // policies, and the coverage (its period words and Remaining follow the plan).
    const handlePlanSaved = (updated) => {
        if (selectedPolicyID) {
            setPolicyOverrides((prev) => ({ ...prev, [selectedPolicyID]: { ...(prev[selectedPolicyID] || {}), ...(updated || {}) } }));
        }
        if (onPoliciesChanged) onPoliciesChanged();
        setPlanDrawerOpen(false);
        const bId = activeBreakdownId();
        if (selectedPolicyID && bId) fetchBreakdownCoverage(selectedPolicyID, bId);
    };

    // One Member/Patient row saved from the panel: the tab follows at once and
    // the panel stays open.
    const handlePlanFieldSaved = (updated) => {
        if (selectedPolicyID) {
            setPolicyOverrides((prev) => ({ ...prev, [selectedPolicyID]: { ...(prev[selectedPolicyID] || {}), ...(updated || {}) } }));
        }
        if (onPoliciesChanged) onPoliciesChanged();
    };

    // Coverage by category, edited in place: the plan's part (shared maximum,
    // ortho terms) on the policy, then the categories' figures as this
    // breakdown's overrides — persistCategoryOverrides refetches the table.
    async function handleSaveCoverage(changes) {
        if (Object.keys(changes.policy || {}).length) {
            const res = await rootController.updatePatientInsurancePolicy(selectedPolicyID,
                { ...changes.policy, breakdown_id: activeBreakdownId() });
            if (!res || res.status !== 200) {
                message.error((res && res.json && res.json.error) || "Couldn't save the coverage");
                return false;
            }
            setPolicyOverrides((prev) => ({ ...prev, [selectedPolicyID]: { ...(prev[selectedPolicyID] || {}), ...(res.json || {}) } }));
            if (onPoliciesChanged) onPoliciesChanged();
        }
        if (Object.keys(changes.set || {}).length || (changes.revert || []).length) {
            const next = { ...(categoryOverrides || {}), ...changes.set };
            (changes.revert || []).forEach((c) => { delete next[c]; });
            const ok = await persistCategoryOverrides(next);
            if (ok) setHasBasciMajorOrthoUpdated(true);
            return ok;
        }
        await fetchBreakdownCoverage(selectedPolicyID, activeBreakdownId());
        return true;
    }

    // The breakdown sheet's header: whose plan, which carrier, where the answers
    // came from, the outcome buttons. Fixed at the top of the pane.
    const renderFinanceCards = () => {
        if (!selectedPolicyID) return null;
        const workingBreakdownId = selectedBreakdownID || newBreakdownID;
        const policyObj = (patientPolicies || []).find((p) => p.id === selectedPolicyID);
        const breakdownEntry = (breakdownsList || []).find((b) => b.id === workingBreakdownId) || null;
        return (
            <SheetHeader
                policy={selectedPolicy}
                breakdown={breakdownEntry}
                // Which of our people handled it is for super admins only.
                handledBy={viewer.isSuperAdmin ? <HandledByChip kind="breakdown" id={workingBreakdownId} /> : null}
                actions={(
                    <>
                        {/* Print PDF · Copy Image · Copy Text, across from the
                            breakdown's source and date. */}
                        <BreakdownExportButtons
                            controls={{
                                printPDF: handlePrintPDF,
                                copyImage: handleCopyImage,
                                copyText: handleCopyText,
                                // A breakdown must be selected: with none there is
                                // nothing to export but the policy block.
                                canPrint: !!workingBreakdownId,
                                isPrinting: isPrintingPDF,
                                isCopyingImage,
                                isCopyingText,
                                mode: customExports ? "custom" : "standard",
                                formatChoice: viewer.isSuperAdmin && officeFormat.versions && officeFormat.versions.length
                                    ? {
                                        value: formatChoice,
                                        options: formatChoiceOptions(officeFormat.versions),
                                        onChange: setFormatChoice,
                                    }
                                    : null,
                            }}
                        />
                        {/* Mark Verified / Details Needed / Unable to obtain are
                            our team's actions: not in the office's Insurance
                            Verification view (reader mode), where the office
                            reads the answer it asked for. */}
                        {workingBreakdownId && !hideVerificationTools ? (
                            <span className="bs-bar-divider" aria-hidden="true" />
                        ) : null}
                        {workingBreakdownId && !hideVerificationTools ? (
                            <OutcomeTagControl
                                patient={patient}
                                successTagName="Breakdown - Verified"
                                policyId={selectedPolicyID}
                                requestType="breakdown"
                                onNoteSaved={onNoteSaved}
                                policyNote={policyObj && policyObj.attention_note}
                                policySuccess={!!(policyObj && policyObj.breakdown_verified_at)}
                                policyNeedsAttention={!!(policyObj && policyObj.needs_attention_at)}
                                policy={policyObj}
                                suggestedNote={outcomeSuggestion && outcomeSuggestion.note}
                                suggestedFields={outcomeSuggestion && outcomeSuggestion.fields}
                            />
                        ) : null}
                        {/* Recalculate intentionally hidden in breakdown lightbox */}
                        {false && (
                            <ApproveButton
                                hasBreakdownID={!!workingBreakdownId}
                                handleRecalculate={handleRecalculate}
                                isApproving={isApproving}
                            />
                        )}
                    </>
                )}
            />
        );
    };

    // Member | Patient | Plan details, Coverage conditions, then coverage by
    // category. Scrolls with the procedures, the way a breakdown reads.
    const renderPlanCard = () => {
        if (!selectedPolicyID) return null;
        if (isLoadingPolicyCoverage && coverageLoadedFor !== `${selectedPolicyID}-${selectedBreakdownID || newBreakdownID}`) {
            return (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "32px 0" }}>
                    <Spin />
                </div>
            );
        }
        return (
            <div className="bs-doc">
                <PlanSummary
                    policy={selectedPolicy}
                    patient={patient}
                    rows={coverageRows}
                    maxResetDate={maxResetDate}
                    intake={intake}
                    onEditPlan={openPlanDetails}
                />
                <CoverageTable
                    key={`${selectedPolicyID}-${selectedBreakdownID || newBreakdownID}`}
                    policy={selectedPolicy}
                    categories={basicMajorOrthoData}
                    sharedMaxGroups={sharedMaxGroups}
                    rows={coverageRows}
                    onSaveCoverage={handleSaveCoverage}
                />
            </div>
        );
    };

    // "Edit plan": the top of the breakdown — Member | Patient rows edited one
    // at a time, then Plan details and Coverage conditions saved together.
    const renderEditDrawers = () => (
        <Drawer
            open={planDrawerOpen}
            onClose={() => setPlanDrawerOpen(false)}
            title="Edit plan"
            width="min(1120px, 94vw)"
            zIndex={1001}
            destroyOnClose
            styles={{ body: { padding: 0 } }}
        >
            {selectedPolicy && planDrawerOpen ? (
                <PlanEditPanel
                    policy={selectedPolicy}
                    patient={patient}
                    rows={coverageRows}
                    carrierOptions={carrierOptions}
                    isLoadingCarriers={isLoadingCarriers}
                    maxResetDate={maxResetDate}
                    breakdownId={activeBreakdownId()}
                    onSaved={handlePlanSaved}
                    onFieldSaved={handlePlanFieldSaved}
                    onCancel={() => setPlanDrawerOpen(false)}
                />
            ) : null}
        </Drawer>
    );

    // "Add Manually": mounted only while open, so each opening starts fresh
    // (the typing itself is kept per policy until saved).
    const renderManualWizard = () => (manualWizardOpen && selectedPolicy ? (
        <ManualBreakdownWizard
            open
            patient={patient}
            policy={selectedPolicy}
            onClose={() => setManualWizardOpen(false)}
            onCreated={handleManualCreated}
            onFinished={handleManualFinished}
            onPolicySaved={handlePlanFieldSaved}
        />
    ) : null);

    // Patient-scoped attention surface. Lives outside `renderFinanceCards`
    // because that function early-returns null when no policy is selected
    // — which would also hide the note for any patient with zero policies.
    // The card itself hides if there's neither a failed scrape for the
    // currently-selected policy nor a manual note, so this is safe to
    // render unconditionally here.
    const renderAttentionSurface = () => {
        // Notes are per-policy: show the SELECTED policy's own attention note.
        const selPolicy = (patientPolicies || []).find((p) => p.id === selectedPolicyID);
        return (
        <BreakdownFailureCard
            policyId={selectedPolicyID}
            patientId={patient && patient.id}
            patientTags={(patient && patient.tags) || []}
            policyNeedsAttention={!!(selPolicy && selPolicy.needs_attention_at)}
            manualAttentionNote={selPolicy && selPolicy.attention_note}
            manualAttentionNoteSetAt={selPolicy && selPolicy.attention_note_set_at}
            manualAttentionNoteSource={selPolicy && selPolicy.attention_note_source}
            infoAsk={selPolicy && selPolicy.info_ask}
            onNoteSaved={onNoteSaved}
            fillHeight
        />
        );
    };

    const renderRightPanel = () => {
        const workingBreakdownId = selectedBreakdownID || newBreakdownID;

        if (workingBreakdownId) {
            return (
                <BreakdownDetailsModal
                    key={`${workingBreakdownId}-${refreshKey}`}
                    selectedPolicyID={selectedPolicyID}
                    patient={patient}
                    onNoteSaved={onNoteSaved}
                    patientPolicies={patientPolicies}
                    newBreakdownID={workingBreakdownId}
                    setNewBreakdownID={setNewBreakdownID}
                    agentType={agentType}
                    eligibilityData={eligibilityData}
                    isGettingDoctorNote={isGettingDoctorNote}
                    onProceduresChanged={() => {
                        fetchBreakdownCoverage(selectedPolicyID, workingBreakdownId);
                        // Lines can fill plan fields; the policy list elsewhere follows.
                        if (onPoliciesChanged) onPoliciesChanged();
                    }}
                    categories={basicMajorOrthoData}
                    sharedMaxGroups={sharedMaxGroups}
                    policy={selectedPolicy}
                    intake={intake}
                    checkPatientInsuranceEligibility={checkPatientInsuranceEligibility}
                    isBreakdownEligibilityRunning={breakdownModalEligibilityActive}
                    setAgentCancelProcess={setAgentCancelProcess}
                    eligibilityAgent2FAStatus={eligibilityAgent2FAStatus}
                    eligibilityTaskStatus={eligibilityTaskStatus}
                    capabilityMatrix={capabilityMatrix}
                />
            );
        }

        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: 40, textAlign: "center" }}>
                <CheckCircleOutlined style={{ fontSize: 64, color: "#848696", opacity: 0.2, marginBottom: 20 }} />
                <h2 style={{ color: "#848696", fontWeight: 400 }}>Insurance Breakdowns</h2>
                <p style={{ color: "#848696", maxWidth: 400 }}>
                    Select an existing breakdown from the sidebar to view or edit its details.
                    Alternatively, create a new manual breakdown by uploading a file, or use AI Fetch to automatically pull data from the insurance portal.
                </p>
            </div>
        );
    };

    // Print PDF — builds a purpose-made document (print/BreakdownPrintDocument)
    // from the data this modal already holds plus one fresh fetch of the
    // breakdown's rows/files, and hands it to the browser's print dialog through
    // a hidden same-origin frame. It deliberately does NOT clone the live pane:
    // that printed the app's chrome, colours and controls, and inherited every
    // stylesheet in the app (Bootstrap's print rules, antd, the global zoom).
    // Everything an export needs: the modal's own data plus the server's saved
    // rows/files for the working breakdown (not the table's mid-edit state).
    // Shared by Print PDF, Copy Image and both Copy Text formats. Returns null
    // (after telling the user) when nothing is selected or the fetch fails.
    async function loadExportInputs() {
        const workingBreakdownId = selectedBreakdownID || newBreakdownID;
        if (!selectedPolicyID || !workingBreakdownId) return null;
        const res = await rootController.getInsuranceBreakdownsPatientDetails(workingBreakdownId);
        if (!res || res.status >= 250 || !res.json) {
            message.error("Couldn't load the breakdown to export.");
            return null;
        }
        const { patient_procedure_coverages: allRowsRaw, files, ...breakdown } = res.json;
        const allRows = allRowsRaw || [];
        const exportedAt = new Date().toISOString();
        return {
            patient,
            policy: selectedPolicy,
            officeIdentity,
            categories: basicMajorOrthoData,
            sharedMaxGroups,
            maxResetDate,
            rows: allRows.filter((r) => !r.data_unavailable),
            unavailableRows: allRows.filter((r) => r.data_unavailable),
            files: files || [],
            printedAt: exportedAt,
            exportedAt,
            breakdown,
            // An office's print shows a dash where FTC's says "Not asked".
            intake,
        };
    }

    // Which format the exports use: the office's own (when live) unless a super
    // admin chose otherwise. `expected` = the buttons promised the office's format.
    const exportFormatOptions = () => ({
        officeId: formatOfficeId,
        choice: formatChoice,
        expected: customExports,
    });

    async function handlePrintPDF() {
        if (!selectedPolicyID || !(selectedBreakdownID || newBreakdownID)) return;
        setIsPrintingPDF(true);
        try {
            const done = await runPrintExport({
                loadInputs: loadExportInputs,
                title: breakdownDocumentTitle(patient),
                ...exportFormatOptions(),
            });
            if (done && done.warning) message.warning(done.warning);
        } catch (err) {
            console.error("Print failed:", err);
            message.error("Failed to print the breakdown");
        } finally {
            setIsPrintingPDF(false);
        }
    }

    // Copy Image: the breakdown as a PNG — the office's own sheet filled in, or
    // the standard print document — put on the clipboard AND saved to
    // Downloads. The clipboard write is started before any await so the click's
    // user gesture still covers it (see print/clipboard.js).
    async function handleCopyImage() {
        if (!selectedPolicyID || !(selectedBreakdownID || newBreakdownID)) return;
        setIsCopyingImage(true);
        const { blobPromise, copied, result } = startImageExport({
            loadInputs: loadExportInputs,
            filenameOf: (inputs) => breakdownImageFilename({
                patient, policy: selectedPolicy, exportedAt: inputs.exportedAt,
            }),
            ...exportFormatOptions(),
        });
        try {
            const blob = await blobPromise;
            const { filename } = result;
            downloadBlob(blob, filename);
            if (await copied) {
                message.success(`Breakdown image copied and saved as ${filename}`);
            } else {
                message.warning(`Saved to Downloads as ${filename} — copying to the clipboard was blocked by the browser`);
            }
            if (result.warning) message.warning(result.warning);
        } catch (err) {
            if (err && err.message !== NOTHING_TO_EXPORT) {
                console.error("Copy image failed:", err);
                message.error("Failed to build the breakdown image");
            }
        } finally {
            setIsCopyingImage(false);
        }
    }

    // Copy Text: the office's own format when it has one; else "detailed" =
    // the PLAN INFO sheet, "short" = the chart one-liner.
    async function handleCopyText(kind) {
        if (!selectedPolicyID || !(selectedBreakdownID || newBreakdownID)) return;
        setIsCopyingText(true);
        const { textPromise, copied, result } = startTextExport({
            loadInputs: loadExportInputs,
            kind,
            ...exportFormatOptions(),
        });
        try {
            const text = await textPromise;
            const what = result.custom ? "Breakdown text" : kind === "short" ? "Short form" : "Detailed breakdown text";
            if (await copied) {
                message.success(`${what} copied`);
            } else {
                showManualCopy(text, { title: what });
            }
            if (result.warning) message.warning(result.warning);
        } catch (err) {
            if (err && err.message !== NOTHING_TO_EXPORT) {
                console.error("Copy text failed:", err);
                message.error("Failed to build the breakdown text");
            }
        } finally {
            setIsCopyingText(false);
        }
    }


    const innerPane = (
        <div style={{ display: "flex", width: "100%", height: "100%" }}>
            {/* Sidebar */}
            {renderSidebar()}

            {/* Main Content Area */}
            <div style={{ flex: 1, backgroundColor: "#fafbfc", overflowY: "auto", position: "relative", display: "flex", flexDirection: "column" }}>
                {/* Close inside the right panel, top-right. Hidden when embedded
                 * — InsuranceMasterModal's header owns Close. (The export
                 * buttons sit in the breakdown's own header row.) */}
                {!embedded && (
                    <div style={{ position: "absolute", top: 15, right: 15, zIndex: 10, display: "flex", gap: 8 }}>
                        <Button
                            onClick={() => setShowInsuranceBreakdownModal(false)}
                        >
                            Close
                        </Button>
                    </div>
                )}

                {/* Attention surface now lives in the left column
                 * (between the policy dropdown and the default card)
                 * so it doesn't shift the finance cards down and
                 * break their alignment with the sidebar. */}

                {/* Policy-level Finance Cards + Recalculate — always visible when a policy is selected */}
                <div ref={summaryRef}>{renderFinanceCards()}</div>

                {/* The breakdown sheet, one white document: the plan summary, coverage, then the procedures. */}
                <div style={{ flex: 1, overflowY: "auto", background: "#fff", paddingBottom: 24 }}>
                    {renderPlanCard()}
                    {renderRightPanel()}
                </div>
                {renderEditDrawers()}
                {renderManualWizard()}
            </div>
        </div>
    );

    return (
        <>
            {embedded ? (
                innerPane
            ) : (
                <Modal
                    open={showInsuranceBreakdownModal}
                    width={1400}
                    zIndex={1000}
                    style={{ top: 12, padding: 0, paddingBottom: 0 }}
                    bodyStyle={{
                        padding: 0,
                        height: "115vh",
                        maxHeight: "115vh",
                        display: "flex",
                        overflow: "hidden",
                    }}
                    footer={null}
                    closeIcon={false}
                    destroyOnClose
                >
                    {innerPane}
                </Modal>
            )}

            {/* Confirm Recalculate Modal — at master level since finance data is here */}
            <ConfirmRecalculateModal
                setShowConfirmRecalculateModal={setShowConfirmRecalculateModal}
                showConfirmRecalculateModal={showConfirmRecalculateModal}
                setConfirmRecalculate={setConfirmRecalculate}
            />

            <BreakdownRequestModal
                setShowNewBreakdownModal={setShowNewBreakdownModal}
                showNewBreakdownModal={showNewBreakdownModal}
                setNewBreakdownID={setNewBreakdownID}
                selectedPolicyID={selectedPolicyID}
                patient={patient}
            />

            {/* The request composer, stacked over this modal. Portals to body, so
                only its zIndex matters — see useRequestComposer. */}
            {composer}

        </>
    );
}
